/* SeasonalOutlook — Phase 60.5 redesign. Vertical list of monthly rows
 * so all 7 months are visible without horizontal scroll. Each row has:
 *   - Month label (left)
 *   - Temp row: number + norm + plain-English anomaly
 *   - Rain row: number + norm + plain-English anomaly
 * Compact, scannable, no edge cutoff.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ThermometerSimple, Drop } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const RAIN_TONE = {
  wet_high:  "#0369a1",
  wet:       "#0891b2",
  neutral:   "#64748b",
  dry:       "#d97706",
  dry_high:  "#b45309",
};
const TEMP_TONE = {
  hot_high:  "#dc2626",
  hot:       "#f97316",
  neutral:   "#64748b",
  cold:      "#0891b2",
  cold_high: "#0369a1",
};

export default function SeasonalOutlook({ location, theme }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!location?.latitude || !location?.longitude) return;
    const ctrl = new AbortController();
    setStatus("loading");
    fetch(
      `${BACKEND}/api/seasonal?lat=${location.latitude}&lon=${location.longitude}`,
      { signal: ctrl.signal },
    )
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        setData(j);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
    return () => ctrl.abort();
  }, [location?.latitude, location?.longitude]);

  if (status === "loading") {
    return (
      <div className="flex flex-col gap-1.5" data-testid="seasonal-loading">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl animate-pulse"
            style={{
              background: theme.surface,
              border: `1px solid ${theme.surfaceBorder}`,
              height: 56,
            }}
          />
        ))}
      </div>
    );
  }

  if (status === "error" || !data?.months?.length) return null;

  return (
    <div data-testid="seasonal-outlook" className="flex flex-col gap-1.5">
      {data.months.map((m, i) => {
        const tempTone = TEMP_TONE[m.temp_anomaly?.tone] || theme.textSecondary;
        const rainTone = RAIN_TONE[m.rain_anomaly?.tone] || theme.textSecondary;
        const monthShort = (m.month_label || "").split(" ")[0];
        const yearShort = (m.month_label || "").split(" ")[1] || "";

        return (
          <motion.div
            key={m.month_iso}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.04 * i }}
            className="rounded-2xl px-3.5 py-2.5"
            style={{
              background: theme.surface,
              border: `1px solid ${theme.surfaceBorder}`,
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
            }}
            data-testid={`seasonal-month-${i}`}
          >
            <div className="flex items-center gap-3">
              {/* LEFT — month label */}
              <div className="flex-shrink-0" style={{ minWidth: 52 }}>
                <div
                  className="font-display font-bold tracking-tight leading-none"
                  style={{ fontSize: 15, color: theme.textPrimary }}
                >
                  {monthShort}
                </div>
                {yearShort && (
                  <div
                    className="text-[9px] mt-0.5 tracking-wider"
                    style={{ color: theme.textMuted }}
                  >
                    {yearShort}
                  </div>
                )}
              </div>

              {/* RIGHT — two metric rows */}
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                {/* Temp row */}
                <MetricRow
                  theme={theme}
                  Icon={ThermometerSimple}
                  iconTone={tempTone}
                  value={m.forecast_temp_c != null ? `${m.forecast_temp_c}°` : "—"}
                  norm={m.baseline_temp_c != null ? `norm ${m.baseline_temp_c}°` : null}
                  label={m.temp_anomaly?.label}
                  labelTone={tempTone}
                />
                {/* Rain row */}
                <MetricRow
                  theme={theme}
                  Icon={Drop}
                  iconTone={rainTone}
                  value={m.forecast_precip_mm != null ? `${m.forecast_precip_mm}mm` : "—"}
                  norm={m.baseline_precip_mm != null ? `norm ${m.baseline_precip_mm}mm` : null}
                  label={m.rain_anomaly?.label}
                  labelTone={rainTone}
                />
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function MetricRow({ theme, Icon, iconTone, value, norm, label, labelTone }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon size={12} weight="duotone" style={{ color: iconTone, flexShrink: 0 }} />
      <span
        className="font-display tabular-nums shrink-0"
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: theme.textPrimary,
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </span>
      {norm && (
        <span
          className="text-[10px] shrink-0"
          style={{ color: theme.textMuted }}
        >
          {norm}
        </span>
      )}
      <span
        className="text-[10.5px] font-semibold truncate ml-auto text-right"
        style={{ color: labelTone }}
      >
        {label}
      </span>
    </div>
  );
}
